/**
 * Bulk Rescan Hook
 * Handles sequential rescanning of multiple places with progress tracking and cancellation
 */

import { useState, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { rescanPlace } from '@/services/placesApi';
import { placeKeys } from '@/hooks/usePlaces';
import { useNotification } from '@/stores/useNotificationStore';
import type { PlaceListItem } from '@/types/place';

export interface BulkRescanProgress {
  total: number;
  completed: number;
  successful: number;
  failed: number;
  currentPlace: PlaceListItem | null;
  isRunning: boolean;
  isCancelled: boolean;
}

export interface BulkRescanResult {
  total: number;
  successful: number;
  failed: number;
  cancelled: boolean;
}

export function useBulkRescan() {
  const queryClient = useQueryClient();
  const { success, error, info, warning } = useNotification();

  const [progress, setProgress] = useState<BulkRescanProgress>({
    total: 0,
    completed: 0,
    successful: 0,
    failed: 0,
    currentPlace: null,
    isRunning: false,
    isCancelled: false,
  });

  // Use ref to track cancellation (avoids stale closure issues)
  const cancelledRef = useRef(false);

  const startBulkRescan = useCallback(async (places: PlaceListItem[]): Promise<BulkRescanResult> => {
    if (places.length === 0) {
      warning('No places', 'No places to rescan');
      return { total: 0, successful: 0, failed: 0, cancelled: false };
    }

    // Reset state
    cancelledRef.current = false;
    setProgress({
      total: places.length,
      completed: 0,
      successful: 0,
      failed: 0,
      currentPlace: null,
      isRunning: true,
      isCancelled: false,
    });

    info('Starting bulk rescan', `Rescanning ${places.length} places...`);

    let successful = 0;
    let failed = 0;

    for (let i = 0; i < places.length; i++) {
      // Check for cancellation
      if (cancelledRef.current) {
        setProgress(prev => ({
          ...prev,
          isRunning: false,
          isCancelled: true,
          currentPlace: null,
        }));

        warning('Rescan cancelled', `Stopped after ${i} of ${places.length} places`);

        return {
          total: places.length,
          successful,
          failed,
          cancelled: true,
        };
      }

      const place = places[i];

      // Update current place
      setProgress(prev => ({
        ...prev,
        currentPlace: place,
      }));

      try {
        const result = await rescanPlace(place.id);

        if (result.success) {
          successful++;
        } else {
          failed++;
        }
      } catch (err) {
        failed++;
        console.error(`Failed to rescan place ${place.id}:`, err);
      }

      // Update progress
      setProgress(prev => ({
        ...prev,
        completed: i + 1,
        successful,
        failed,
      }));

      // Small delay between requests to avoid overwhelming the server
      if (i < places.length - 1 && !cancelledRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Completed
    setProgress(prev => ({
      ...prev,
      isRunning: false,
      currentPlace: null,
    }));

    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: placeKeys.lists() });

    if (failed === 0) {
      success('Bulk rescan complete', `Successfully rescanned ${successful} places`);
    } else {
      warning(
        'Bulk rescan complete',
        `Rescanned ${successful} places, ${failed} failed`
      );
    }

    return {
      total: places.length,
      successful,
      failed,
      cancelled: false,
    };
  }, [queryClient, success, warning, info]);

  const cancelBulkRescan = useCallback(() => {
    cancelledRef.current = true;
    setProgress(prev => ({
      ...prev,
      isCancelled: true,
    }));
  }, []);

  const resetProgress = useCallback(() => {
    setProgress({
      total: 0,
      completed: 0,
      successful: 0,
      failed: 0,
      currentPlace: null,
      isRunning: false,
      isCancelled: false,
    });
  }, []);

  return {
    progress,
    startBulkRescan,
    cancelBulkRescan,
    resetProgress,
  };
}

export default useBulkRescan;
